import { Translator } from "@yjsgames/i18n";
import koJson from "../data/generated/language_ko.json";
import enJson from "../data/generated/language_en.json";

/**
 * 지원 언어 — 한국어 + 영어 + 아랍어(RTL). 추가 언어는 `data/language_<code>.tsv` 작성 +
 * `npm run export:data` 후 여기에 import + SUPPORTED_LANGUAGES/LANGUAGE_NAMES/LOCALE_TABLES에 1줄씩 추가.
 *
 * 번역 엔진은 재사용 패키지 `@yjsgames/i18n`의 Translator(locale-agnostic). 이 모듈은 앱 특화 부분만:
 * 언어 코드 union, 사전 주입, navigator 자동 감지.
 *
 * ⚠ 아랍어(ar)는 RTL/Bidi/UI 미러링을 켜기 위한 타깃 — 텍스트 방향은 `@yjsgames/i18n resolveDirection`이
 *   언어코드로 판정한다(`i18n.ts getTextDirection`). 본문 사전은 아직 미번역이라 **영어 사전을 재사용**하며,
 *   `data/language_ar.tsv` 작성(후속) 후 교체하면 된다.
 */
export type LanguageCode = "ko" | "en" | "ar";

export const SUPPORTED_LANGUAGES: readonly LanguageCode[] = ["ko", "en", "ar"];

export const DEFAULT_LANGUAGE: LanguageCode = "ko";

/** 드롭다운 표시용 언어명 (각 언어를 그 언어 자체로 표기). */
export const LANGUAGE_NAMES: Record<LanguageCode, string> = {
  ko: "한국어",
  en: "English",
  ar: "العربية",
};

/** generated/language_*.json 한 행 = TSV 한 줄 (key, value). */
interface LocaleRow {
  readonly key: string;
  readonly value: string;
}

function toMap(rows: readonly LocaleRow[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const r of rows) map[r.key] = r.value;
  return map;
}

/**
 * 언어별 key → 문자열 사전. `balance.ts`의 generated JSON 재구성 패턴과 동일.
 * TSV가 SSoT — 값 수정은 `data/language_*.tsv` 후 `npm run export:data`.
 * (Translator에 주입 + locales.test.ts 정합 검증용으로 export 유지.)
 */
export const LOCALE_TABLES: Record<LanguageCode, Record<string, string>> = {
  ko: toMap(koJson as LocaleRow[]),
  en: toMap(enJson as LocaleRow[]),
  // 아랍어 본문 미번역 — 영어 사전 재사용(RTL 레이아웃/방향 데모용). language_ar.tsv 작성 후 교체.
  ar: toMap(enJson as LocaleRow[]),
};

/** 앱 전역 Translator (@yjsgames/i18n) — 사전 주입 + ko fallback. */
export const translator = new Translator({
  dictionaries: LOCALE_TABLES,
  locale: DEFAULT_LANGUAGE,
  fallbackLocale: DEFAULT_LANGUAGE,
});

/**
 * 브라우저 언어로 초기 언어 추정 (세이브에 언어가 없는 첫 부팅용).
 * navigator 미존재(SSR/노드) 또는 미지원 언어면 DEFAULT_LANGUAGE.
 */
export function detectLanguage(): LanguageCode {
  if (typeof navigator !== "undefined" && navigator.language) {
    const lang = navigator.language.toLowerCase();
    if (lang.startsWith("en")) return "en";
    if (lang.startsWith("ko")) return "ko";
    if (lang.startsWith("ar")) return "ar";
  }
  return DEFAULT_LANGUAGE;
}

/** code가 지원 언어인지 검사 (세이브 복원 시 방어). */
export function isLanguageCode(code: string): code is LanguageCode {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(code);
}
